/**
 * ChapterSubPage — reusable template for the four Circular CMF Guide chapters
 * (CX-2726 Reborn, CX-2727 Renewed & Restored, CX-2728 Designed to Last,
 * CX-2729 Digital Twins).
 *
 * CX-2725 owns this template. Each chapter ticket supplies a `ChapterContent`
 * and renders `<ChapterSubPage content={...} />` — no chapter builds its own
 * layout. Missing sections are simply not rendered (empty-state, CX-2725 AC).
 *
 * Route (added per chapter ticket): /circular-cmf-guide/:chapterSlug
 */

import { Breadcrumb } from "components/navigation";
import { TrendHero } from "pages/trend/components/TrendHero";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import type {
  ChapterBlock,
  ChapterContent,
  GradeTable as GradeTableData,
} from "./chapter.types";
import "./ChapterSubPage.css";
import { AnnotatedDiagram } from "./components/AnnotatedDiagram";
import { Checklist } from "./components/Checklist";
import { Columns } from "./components/Columns";
import { GradeTable } from "./components/GradeTable";
import { ImageCarousel } from "./components/ImageCarousel";
import { MaterialCard } from "./components/MaterialCard";
import { MaterialSamplesList } from "./components/MaterialSamplesList";
import { NoteCallout } from "./components/NoteCallout";
import { NumberedMaterialGroup } from "./components/NumberedMaterialGroup";
import { Quote } from "./components/Quote";
import { StoryBlock } from "./components/StoryBlock";
import { SuffixDecoder } from "./components/SuffixDecoder";

interface ChapterSubPageProps {
  content: ChapterContent;
}

const CMF_CONTACT_EMAIL = "cmf-design@covestro.com";

/** Render one ordered body block (2727+). `samplesTitle` is the localized
 *  "Material Samples" heading, threaded in so the block path matches the
 *  fixed-slot path. */
const renderBlock = (
  block: ChapterBlock,
  index: number,
  samplesTitle: string,
) => {
  switch (block.kind) {
    case "subheading":
      return (
        <h2
          key={`subheading-${index}`}
          className="chapter-sub-page__section-title"
        >
          {block.text}
        </h2>
      );
    case "paragraph":
      return (
        <p key={`paragraph-${index}`} className="chapter-sub-page__paragraph">
          {block.text}
        </p>
      );
    case "note":
      return <NoteCallout key={`note-${index}`} note={block.note} />;
    case "quote":
      return <Quote key={`quote-${index}`} quote={block.quote} />;
    case "suffixDecoder":
      return <SuffixDecoder key={`suffix-${index}`} decoder={block.decoder} />;
    case "gradeTable":
      return (
        <GradeTable
          key={block.table.title ?? `grade-${index}`}
          table={block.table}
        />
      );
    case "story":
      // `flush` — no card surface so the heading left-aligns with the other
      // sections in the band (Figma). A story block's `callout` (if any) is
      // authored as a separate band-level `note` block (Figma 1657-10990).
      return <StoryBlock key={`story-${index}`} story={block.story} flush />;
    case "materialGroup":
      return (
        <NumberedMaterialGroup
          key={`material-group-${index}`}
          title={block.group.title}
          primary={block.group.primary}
          secondary={block.group.secondary}
        />
      );
    case "materialCards":
      return block.cards.length > 0 ? (
        <div
          key={`material-cards-${index}`}
          className="chapter-sub-page__cards"
        >
          {block.cards.map((card) => (
            <MaterialCard key={card.title} card={card} />
          ))}
        </div>
      ) : null;
    case "samples":
      return block.samples.length > 0 ? (
        <MaterialSamplesList
          key={`samples-${index}`}
          samples={block.samples}
          title={block.title ?? samplesTitle}
        />
      ) : null;
    case "checklist":
      return <Checklist key={`checklist-${index}`} items={block.items} />;
    case "columns":
      return (
        <Columns
          key={`columns-${index}`}
          left={block.left.map((child, i) =>
            renderBlock(child, i, samplesTitle),
          )}
          right={block.right.map((child, i) =>
            renderBlock(child, i, samplesTitle),
          )}
        />
      );
    case "annotatedDiagram":
      return (
        <AnnotatedDiagram
          key={`diagram-${index}`}
          title={block.title}
          diagram={block.diagram}
        />
      );
    case "imageCarousel":
      return (
        <ImageCarousel
          key={`carousel-${index}`}
          title={block.title}
          carousel={block.carousel}
        />
      );
    case "band":
      // Full-width surface band (Reborn-style). Child blocks render inside a
      // centered content column so every section shares one content width.
      return (
        <section
          key={`band-${index}`}
          className={`chapter-sub-page__band chapter-sub-page__band--${block.surface}`}
        >
          <div className="chapter-sub-page__band-inner">
            {block.blocks.map((child, i) =>
              renderBlock(child, i, samplesTitle),
            )}
          </div>
        </section>
      );
    default:
      return null;
  }
};

export const ChapterSubPage = ({ content }: ChapterSubPageProps) => {
  const { t } = useTranslation();

  // Support both the new `gradeTables` array and the legacy single `gradeTable`.
  const gradeTables: GradeTableData[] =
    content.gradeTables ?? (content.gradeTable ? [content.gradeTable] : []);

  return (
    <div className="chapter-sub-page">
      {/* Breadcrumb — Homepage / Circular CMF Guide / <chapter> */}
      <Breadcrumb
        items={[
          { label: t("common_homepage", "Homepage"), to: "/", home: true },
          {
            label: t("cmf_guide_breadcrumb", "Circular CMF Guide"),
            to: "/circular-cmf-guide",
          },
          { label: content.title },
        ]}
      />

      {/* Back to guide */}
      <div className="chapter-sub-page__back">
        <Link className="chapter-sub-page__back-link" to="/circular-cmf-guide">
          <svg
            width="13"
            height="13"
            viewBox="0 0 14 14"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M11 7H3M6.5 3.5L3 7l3.5 3.5"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {t("cmf_guide_back", "Back to Circular CMF Guide")}
        </Link>
      </div>

      {/* Hero — reuses the Trend template hero */}
      <TrendHero
        heroImage={content.heroImage}
        title={content.title}
        chip={content.eyebrow ?? t("cmf_guide_chip", "Sustainability Guide")}
        description={content.intro}
        tags={content.tags}
        tagLabel={t("cmf_guide_tag", "CMF Library")}
        exploreLabel={t("cmf_guide_scroll_to_section", "Scroll to section")}
        showExplore
        exploreVariant="primary"
        collapsible
      />

      {content.blocks ? (
        // When the blocks are full-width `band`s (Reborn-style alternating
        // surfaces), render them directly; each band owns its centered content
        // column. Otherwise render the blocks in the single centered body.
        content.blocks.every((block) => block.kind === "band") ? (
          content.blocks.map((block, i) =>
            renderBlock(
              block,
              i,
              t("cmf_guide_samples_title", "Material Samples"),
            ),
          )
        ) : (
          <div className="chapter-sub-page__body">
            {content.blocks.map((block, i) =>
              renderBlock(
                block,
                i,
                t("cmf_guide_samples_title", "Material Samples"),
              ),
            )}
          </div>
        )
      ) : (
        /* Fixed-slot layout (Reborn) — full-width alternating bands that match
           the Figma section rhythm (node 1657-9181): grades on white, case
           study on #fafafa, samples on #f3f3f3. Each band centers its content
           in a shared 1100px column so every section left-aligns. Bands with
           no content are not rendered (empty-state, CX-2725 AC). */
        <>
          {/* Band A — grade tables + processing note (white, Figma 1657-9261) */}
          {(content.gradeSectionTitle ||
            content.suffixDecoder ||
            gradeTables.length > 0 ||
            (content.notes?.length ?? 0) > 0) && (
            <section className="chapter-sub-page__band chapter-sub-page__band--white">
              <div className="chapter-sub-page__band-inner">
                {(content.gradeSectionTitle ||
                  content.suffixDecoder ||
                  gradeTables.length > 0) && (
                  <section className="chapter-sub-page__grades">
                    {content.gradeSectionTitle && (
                      <h2 className="chapter-sub-page__section-title">
                        {content.gradeSectionTitle}
                      </h2>
                    )}
                    {content.suffixDecoder && (
                      <SuffixDecoder decoder={content.suffixDecoder} />
                    )}
                    {gradeTables.map((table, i) => (
                      <GradeTable
                        key={table.title ?? `grade-table-${i}`}
                        table={table}
                      />
                    ))}
                  </section>
                )}

                {content.notes?.map((note) => (
                  <NoteCallout key={note.text} note={note} />
                ))}
              </div>
            </section>
          )}

          {/* Band B — case study + material group (#fafafa, Figma 1657-9469) */}
          {(content.story || content.materialGroup) && (
            <section className="chapter-sub-page__band chapter-sub-page__band--gray">
              <div className="chapter-sub-page__band-inner">
                {content.story && <StoryBlock story={content.story} flush />}

                {/* Story callout at band level so it left-aligns with the
                    story heading and material group (Figma node 1657-10990). */}
                {content.story?.callout && (
                  <NoteCallout note={content.story.callout} />
                )}

                {content.materialGroup && (
                  <NumberedMaterialGroup
                    title={content.materialGroup.title}
                    primary={content.materialGroup.primary}
                    secondary={content.materialGroup.secondary}
                  />
                )}
              </div>
            </section>
          )}

          {/* Band C — material samples (#f3f3f3, Figma 1657-10850) */}
          {((content.materialCards?.length ?? 0) > 0 ||
            (content.samples?.length ?? 0) > 0) && (
            <section className="chapter-sub-page__band chapter-sub-page__band--light">
              <div className="chapter-sub-page__band-inner">
                {content.materialCards && content.materialCards.length > 0 && (
                  <div className="chapter-sub-page__cards">
                    {content.materialCards.map((card) => (
                      <MaterialCard key={card.title} card={card} />
                    ))}
                  </div>
                )}

                {content.samples && content.samples.length > 0 && (
                  <MaterialSamplesList
                    samples={content.samples}
                    title={t("cmf_guide_samples_title", "Material Samples")}
                  />
                )}
              </div>
            </section>
          )}
        </>
      )}

      {/* Contact CTA — full-width, white bg, top border (Figma FooterCta) */}
      <div className="chapter-sub-page__contact">
        <div className="chapter-sub-page__band-inner">
          <p className="chapter-sub-page__contact-text">
            {t(
              "cmf_guide_contact",
              "Questions about specific grades or color feasibility? Contact our CMF experts:",
            )}{" "}
            <a
              className="chapter-sub-page__contact-link"
              href={`mailto:${CMF_CONTACT_EMAIL}`}
            >
              {CMF_CONTACT_EMAIL}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};
